<template>
  <div>
    <div class="dept-about-modal" @click.self="close">
      <div
        class="dept-about-modal__card"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <button type="button" class="dept-about-modal__close" aria-label="Закрыть" @click="close">
          ×
        </button>

        <h2 :id="titleId" class="dept-about-modal__title">{{ heading }}</h2>
        <p v-if="text" class="dept-about-modal__text">{{ text }}</p>

        <div v-if="slides.length" class="dept-about-modal__gallery">
          <div class="dept-about-modal__viewport">
            <button
              v-if="slides.length > 1"
              type="button"
              class="dept-about-modal__arrow dept-about-modal__arrow--prev"
              aria-label="Предыдущее фото"
              @click="goPrev"
            >
              ‹
            </button>
            <button
              v-if="slides.length > 1"
              type="button"
              class="dept-about-modal__arrow dept-about-modal__arrow--next"
              aria-label="Следующее фото"
              @click="goNext"
            >
              ›
            </button>
            <div class="dept-about-modal__mask">
              <div
                class="dept-about-modal__track"
                :style="{ transform: 'translateX(-' + (100 * currentIndex) + '%)' }"
              >
                <div
                  v-for="(slide, i) in slides"
                  :key="'dept-about-slide-' + i"
                  class="dept-about-modal__slide"
                >
                  <button
                    type="button"
                    class="dept-about-modal__img-wrap"
                    :aria-label="slideZoomLabel(slide)"
                    @click="openLightbox(i)"
                  >
                    <img
                      :src="slide.src"
                      :alt="slide.alt"
                      class="dept-about-modal__img"
                    >
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="slides.length > 1"
            class="dept-about-modal__dots"
            role="tablist"
            aria-label="Фото галереи"
          >
            <button
              v-for="(slide, i) in slides"
              :key="'dept-about-dot-' + i"
              type="button"
              class="dept-about-modal__dot"
              :class="{ 'dept-about-modal__dot--active': i === currentIndex }"
              :aria-label="'Фото ' + (i + 1)"
              :aria-current="i === currentIndex ? 'true' : undefined"
              @click="goTo(i)"
            />
          </div>
        </div>
      </div>
    </div>
    <LightBox
      ref="lightbox"
      :media="slides"
      :show-light-box="lightboxVisible"
      :site-loading="siteLoading"
      @close="closeLightbox"
    />
  </div>
</template>

<script>
import LightBox from 'vue-image-lightbox'
import 'vue-image-lightbox/dist/vue-image-lightbox.min.css'
import siteLoading from '@/assets/img/siteloading.gif'

let modalUid = 0

export default {
  name: 'DepartmentAboutModal',
  components: { LightBox },
  props: {
    aboutHeading: { type: String, default: '' },
    aboutText: { type: String, default: '' },
    gallery: { type: Array, default: () => [] },
  },
  data() {
    modalUid += 1
    return {
      currentIndex: 0,
      lightboxVisible: false,
      siteLoading,
      titleId: 'dept-about-modal-title-' + modalUid,
      prevBodyOverflow: '',
    }
  },
  computed: {
    heading() {
      const h = String(this.aboutHeading || '').trim()
      return h || 'Об отделении'
    },
    text() {
      return String(this.aboutText || '').trim()
    },
    slides() {
      const raw = Array.isArray(this.gallery) ? this.gallery : []
      return raw
        .map((item) => {
          const src = String((item && item.image) || '').trim()
          const alt = String((item && item.alt) || '').trim()
          return {
            src,
            thumb: src,
            alt: alt || this.heading,
            caption: alt,
          }
        })
        .filter((item) => item.src)
    },
  },
  watch: {
    slides() {
      if (this.currentIndex >= this.slides.length) {
        this.currentIndex = Math.max(0, this.slides.length - 1)
      }
    },
  },
  mounted() {
    this.prevBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    document.body.style.overflow = this.prevBodyOverflow
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    close() {
      if (this.lightboxVisible) return
      this.$emit('close')
    },
    onKeydown(e) {
      if (e.key !== 'Escape') return
      if (this.lightboxVisible) return
      this.close()
    },
    slideZoomLabel(slide) {
      return slide.alt ? `Увеличить: ${slide.alt}` : 'Увеличить фото'
    },
    openLightbox(index) {
      if (!this.slides.length) return
      this.lightboxVisible = true
      this.$nextTick(() => {
        if (this.$refs.lightbox) {
          this.$refs.lightbox.showImage(index)
        }
      })
    },
    closeLightbox() {
      this.lightboxVisible = false
    },
    goTo(i) {
      if (i < 0 || i >= this.slides.length) return
      this.currentIndex = i
    },
    goNext() {
      const n = this.slides.length
      if (n < 2) return
      this.currentIndex = (this.currentIndex + 1) % n
    },
    goPrev() {
      const n = this.slides.length
      if (n < 2) return
      this.currentIndex = (this.currentIndex - 1 + n) % n
    },
  },
}
</script>

<style scoped lang="scss">
$green-text: #336633;
$text: #2d3436;
$muted: #6b7280;
$hero-btn-dark: #2d5a27;

.dept-about-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.72);
}

.dept-about-modal__card {
  position: relative;
  width: min(860px, 100%);
  max-height: 90vh;
  overflow: auto;
  padding: 28px 28px 24px;
  background: #fff;
  border-radius: 16px;
  box-sizing: border-box;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}

.dept-about-modal__close {
  position: absolute;
  top: 10px;
  right: 14px;
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: $muted;
  cursor: pointer;

  &:hover {
    color: $text;
  }
}

.dept-about-modal__title {
  margin: 0 36px 16px 0;
  font-family: "TTDrugs", sans-serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  color: $green-text;
}

.dept-about-modal__text {
  margin: 0 0 20px;
  font-family: "Circe", sans-serif;
  font-size: 16px;
  line-height: 1.65;
  color: $text;
  white-space: pre-line;
}

.dept-about-modal__gallery {
  margin-top: 8px;
}

.dept-about-modal__viewport {
  position: relative;
}

.dept-about-modal__mask {
  overflow: hidden;
  border-radius: 12px;
  background: #eef3ee;
}

.dept-about-modal__track {
  display: flex;
  transition: transform 0.35s ease;
}

.dept-about-modal__slide {
  flex: 0 0 100%;
  min-width: 100%;
  box-sizing: border-box;
}

.dept-about-modal__img-wrap {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
}

.dept-about-modal__img {
  display: block;
  width: 100%;
  height: 320px;
  object-fit: cover;
  object-position: center;

  @media (max-width: 640px) {
    height: 220px;
  }
}

.dept-about-modal__arrow {
  position: absolute;
  top: 50%;
  z-index: 2;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: $hero-btn-dark;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);

  &:hover {
    background: #fff;
  }

  &--prev {
    left: 10px;
  }

  &--next {
    right: 10px;
  }
}

.dept-about-modal__dots {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.dept-about-modal__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #c5d0c5;
  cursor: pointer;

  &--active {
    background: $hero-btn-dark;
  }
}
</style>
